import { Codetable } from '../types-api';
import { FavouriteDefinition, ApiResponse, FavouriteLayoutModel } from 'app/types-shared';
import { RemoteData } from 'app/infrastructure/remote-data';
import { DomainNode, GroupByDomain, MetadataResult } from '../types';
import { LayoutType } from 'app/area/reports/containers/chart/types-store';
import { FavouriteDataModel } from 'app/components/favourite-layout/favourite/common/favourite-interfaces';
import { ReportCommonApi } from 'app/area/reports/types-api';
import { DrillDownModel, DrillDownResult } from 'app/components/favourite-layout/favourite/common/drill-down-model';

export const endpoint = {
  aggregateDefaultFields: 'favourite/definition/draft/frequencyreport/aggregatefield/options/default',
  aggregateFields: 'favourite/definition/draft/frequencyreport/aggregatefield/options',
  aggregateFunctionOptionsFor: (domainId: number) =>
    `favourite/definition/draft/frequencyreport/aggregatefield/${domainId}/aggregatefunction/options`,
  uomSetOptionsFor: (domainId: number) =>
    `favourite/definition/draft/selectionreport/aggregatefield/${domainId}/uomset/options`,
  distributionDefaultFields: 'favourite/definition/draft/frequencyreport/exposureapplication/options/default',
  distributionFields: 'favourite/definition/draft/frequencyreport/exposureapplication/options',
  metadata: (applicationId: number) => `favourite/definition/draft/frequencyreport/exposureapplication/${applicationId}/metadata`,

  groupByDefaultFields: 'favourite/definition/draft/frequencyreport/groupbyfields/options/default',
  groupBySortOrderType: 'favourite/definition/draft/sortordertype/options',
  groupByFields: 'favourite/definition/draft/frequencyreport/groupbyfields/options',
  groupBySelections: (domainId: number) =>
    `favourite/definition/draft/frequencyreport/groupbyfield/${domainId}/selectiontypes`,

  targetDefaultFields: 'favourite/definition/draft/frequencyreport/target/options/default',
  targetFields: 'favourite/definition/draft/frequencyreport/target/options',

  statisticalReport: 'statisticalReport',
  loadLayoutTypes: (reportId: number) =>
    `favourite/definition/draft/report/${reportId}/layouttype/options`,
  report: 'report',
  dashboard: 'dashboard',
  reportCategory: 'report/options'
};

export const api: ReportCommonApi = {
  loadAggregateDefaultFields: async (): Promise<ApiResponse<DomainNode[]>> => {
    return await new RemoteData(endpoint.aggregateDefaultFields)
      .resource<ApiResponse<DomainNode[]>>()
      .all();
  },
  loadAggregateFields: async (query: string): Promise<ApiResponse<DomainNode[]>> => {
    return await new RemoteData(`${endpoint.aggregateFields}${!!query ? '/search' : ''}`)
      .resource<ApiResponse<DomainNode[]>>()
      .filter({
        query
      }).all();
  },
  loadAggregateFunctionOptions: async (domainId: number): Promise<ApiResponse<Codetable>> => {
    return await new RemoteData(endpoint.aggregateFunctionOptionsFor(domainId))
      .resource<ApiResponse<Codetable>>()
      .all();
  },

  loadUomSetOptions: async (domainId: number): Promise<ApiResponse<Codetable>> => {
    return await new RemoteData(endpoint.uomSetOptionsFor(domainId))
      .resource<ApiResponse<Codetable>>()
      .all();
  },

  /** Distribution */

  loadDistributionDefaultFields: async (): Promise<ApiResponse<Codetable>> => {
    return await new RemoteData(endpoint.distributionDefaultFields)
      .resource<ApiResponse<Codetable>>()
      .all();
  },
  loadDistributionFields: async (query?: string): Promise<ApiResponse<Codetable>> => {
    return await new RemoteData(`${endpoint.distributionFields}${!!query ? '/search' : ''}`)
      .resource<ApiResponse<Codetable>>()
      .filter({
        query
      }).all();
  },
  loadMetadata: async (applicaitonId: number): Promise<ApiResponse<MetadataResult>> => {
    return await new RemoteData(endpoint.metadata(applicaitonId))
      .resource<ApiResponse<MetadataResult>>()
      .all();
  },

  /** Group by */

  loadGroupByDefaultFields: async (
    exposureApplication?: number,
    aggregateField?: number,
    selectionAggrType?: number,
    caseTypeIds?: number[]
  ): Promise<ApiResponse<DomainNode[]>> => {
    return await new RemoteData(endpoint.groupByDefaultFields)
      .resource<ApiResponse<DomainNode[]>>()
      .filter({
        exposureApplication,
        aggregateField,
        selectionAggrType,
        applicationId: caseTypeIds
      })
      .all();
  },
  loadGroupBySortOrderType: async (): Promise<ApiResponse<Codetable>> => {
    return await new RemoteData(endpoint.groupBySortOrderType)
      .resource<ApiResponse<Codetable>>()
      .all();
  },
  loadGroupByFields: async (
    query: string, 
    exposureApplication?: number,
    aggregateField?: number,
    selectionAggrType?: number,
    caseTypeIds?: number[],
    applicationExcluded?: boolean
  ): Promise<ApiResponse<DomainNode[]>> => {
    
    return await new RemoteData(
      `${endpoint.groupByFields}${!!query ? '/search' : ''}`
    )
      .resource<ApiResponse<DomainNode[]>>()
      .filter({
        query,
        exposureApplication,
        aggregateField,
        selectionAggrType,
        applicationId: caseTypeIds,
        ...(applicationExcluded && { applicationExcluded })
      }).all();
  },
  loadGroupBySelectionTypes: async (domainId: number, caseTypeIds: number[]): Promise<ApiResponse<GroupByDomain>> => {
    return await new RemoteData(endpoint.groupBySelections(domainId))
      .resource<ApiResponse<GroupByDomain>>()
      .filter({
        applicationId: caseTypeIds
      })
      .all();
  },

  /** Target */

  loadTargetDefaultFields: async (aggregationId: number): Promise<ApiResponse<Codetable>> => {
    return await new RemoteData(endpoint.targetDefaultFields)
      .resource<ApiResponse<Codetable>>()
      .filter({
        selectionAggrType: aggregationId
      })
      .all();
  },
  loadTargetFields: async (aggregationId: number, query: string): Promise<ApiResponse<Codetable>> => {
    return await new RemoteData(`${endpoint.targetFields}${!!query ? '/search' : ''}`)
      .resource<ApiResponse<Codetable>>()
      .filter({
        selectionAggrType: aggregationId,
        query
      }).all();
  },

  /** Chart */

  reportLayout: async (favouriteId: number, report: number,
    layout: number, layoutTypeContextId: number): Promise<FavouriteLayoutModel> => {
    const response = await new RemoteData(endpoint.report)
      .resource<ApiResponse<FavouriteLayoutModel>>()
      .withId(`${report}/layout/${layout}`)
      .filter({
        layoutTypeContextId,
        favouriteId
      })
      .single();

    return response.data;
  },
  loadLayoutTypes: async (reportId: number): Promise<ApiResponse<LayoutType[]>> => {
    return await new RemoteData(endpoint.loadLayoutTypes(reportId))
      .resource<ApiResponse<LayoutType[]>>()
      .all();
  },
  executeFavourite: async (definition: FavouriteDefinition): Promise<FavouriteDataModel> => {

    const response = await new RemoteData(endpoint.statisticalReport).resource<any>()
      .create({ favouriteDefinition: definition }, false);

    const json = await response.json();

    json.data.id = json.data.favourite;

    return json.data;
  },

  /**
   * Drill down from dashboard.
   */
  drillDown: async (dashboardId: number, favouriteId: number, drillDownModel: DrillDownModel): Promise<DrillDownResult> => {
    const response: any = await new RemoteData(`${endpoint.dashboard}/${dashboardId}/favourite/${favouriteId}/drilldown`)
      .resource<ApiResponse<DrillDownResult>>()
      .create(drillDownModel, false);

    const json = await response.json();

    return json.data;
  },

  loadOptionsDrillDownTo: async (): Promise<ApiResponse<Codetable>> => {
    return await new RemoteData(endpoint.reportCategory)
      .resource<ApiResponse<Codetable>>()
      .filter({ allowDrillDownTo : true })
      .all();
  }
};
